import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { PortfolioTab } from 'components/portfolio/Portfolio';
import { PatientState } from 'constants/filterKeysConstants';
import { useProfile } from 'context/profile';
import useIsMobile from 'hooks/useIsMobile';
import useIsPortfolio from 'hooks/useIsPortfolio';
import { getPatientCounts, portfolioQueryKeys } from 'services/api/portfolio';
import { useMobileNavigationStore } from 'stores/mobileNavigationStore';
import { usePortfolioStore } from 'stores/portfolioStore';

import { filtersForSelectedTab } from './helpers/filters';

export default function usePortfolioTabs(debouncedSearch = '') {
  const isMobile = useIsMobile();
  const isPortfolio = useIsPortfolio();
  const navigationOpen = useMobileNavigationStore((state) => state.navigationOpen);
  const { profile } = useProfile();
  const filters = usePortfolioStore((state) => state.filters);

  const tabsWithoutLabel = useMemo(() => {
    if (profile.isAffiliateUser) {
      return [
        {
          value: PatientState.LATEST,
          displayName: PatientState.LATEST,
          patientState: PatientState.LATEST,
          locationType: undefined,
        },
      ];
    }

    const options: PortfolioTab[] =
      profile.tabProviderTypes.map((x) => {
        return {
          value: x.apiName,
          displayName: x.displayName,
          groupType: x.id,
          locationType: x.apiName,
          patientState: PatientState.CURRENT,
          groupTypeCreatedAt: x.createdAt ?? '',
        };
      }) ?? [];

    if (profile.canActAsProvider || profile.actingClient.isChildCollaborator) {
      options.push({
        value: PatientState.CONTINUED,
        displayName: 'Continued Care',
        patientState: PatientState.CONTINUED,
        locationType: undefined,
      });
    }

    return options;
  }, [profile]);

  const tabCounts = useQueries({
    queries: tabsWithoutLabel.map((tab) => {
      const tabSpecificFilters = filtersForSelectedTab(filters, tab, profile);
      const tabCountQueryParams = {
        search: debouncedSearch,
        filters: tabSpecificFilters,
      };

      return {
        queryKey: [...portfolioQueryKeys.count(tab.value), profile.actingClientId, tabCountQueryParams],
        queryFn: ({ signal }) => {
          return getPatientCounts({ tab, filters: tabSpecificFilters, search: debouncedSearch, profile }, signal);
        },
        staleTime: 30000,
        enabled: isMobile ? navigationOpen : isPortfolio,
      };
    }),
    combine: (results) => {
      // Only return counts when all queries are complete to avoid intermediate renders
      const allLoaded = results.every((result) => !result.isLoading);
      if (!allLoaded) return undefined;
      return results.map((result) => result.data?.data.meta.totalRecords ?? 0);
    },
  });

  const tabs: PortfolioTab[] = useMemo(() => {
    return tabsWithoutLabel.map((x, i) => {
      return {
        ...x,
        label: tabCounts?.[i] == undefined ? x.displayName : `${x.displayName} (${tabCounts[i]})`,
        count: tabCounts?.[i] ?? 0,
      };
    });
  }, [tabsWithoutLabel, tabCounts]);

  return tabs;
}
